import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { customerCategoryLabel } from '@erp/shared';
import { SearchCombobox } from '@/components/common/search-combobox';
import { usePermissions } from '@/features/auth';
import { useCustomerCategories } from '../api/use-business-terms';
export function CustomerCategorySelect({
  id,
  value,
  onChange,
  disabled = false,
}: {
  id: string;
  value: string | null | undefined;
  onChange: (code: string | null) => void;
  disabled?: boolean;
}): JSX.Element {
  const query = useCustomerCategories();
  const { t, i18n } = useTranslation();
  const { can } = usePermissions();
  const [search, setSearch] = useState('');
  const rows = query.data?.data ?? [];
  const selected = rows.find((row) => row.code === value);
  const label = selected
    ? customerCategoryLabel(selected, i18n.language)
    : value
      ? t(`partners.industries.${value}`, { defaultValue: value })
      : t('common.none');
  const options = [
    { id: 'NONE', label: t('common.none') },
    ...rows
      .filter((row) => row.isActive || row.code === value)
      .map((row) => ({
        id: row.code,
        label:
          customerCategoryLabel(row, i18n.language) +
          (row.isActive ? '' : ` (${t('conditions.inactive')})`),
      })),
    ...(value && !selected ? [{ id: value, label }] : []),
  ].filter(
    (row) =>
      row.id === 'NONE' || row.label.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
  );
  return (
    <div className="space-y-2">
      <SearchCombobox
        id={id}
        value={label}
        placeholder={t('conditions.search')}
        ariaLabel={t('conditions.categoryField')}
        options={options}
        isLoading={query.isPending}
        isError={query.isError}
        onRetry={() => void query.refetch()}
        disabled={disabled}
        emptyLabel={t('conditions.empty')}
        onQueryChange={setSearch}
        onSelect={(option) => {
          onChange(option.id === 'NONE' ? null : option.id);
          setSearch('');
        }}
      />
      {can('settings:read') ? (
        <Link
          className="inline-flex min-h-11 items-center text-sm text-muted-foreground underline underline-offset-4"
          to="/settings/customer-categories"
        >
          {t('conditions.manageCategories')}
        </Link>
      ) : null}
    </div>
  );
}
